<template>
  <div class="studies_bg">
    <mainheader></mainheader>
    <div class="container">
      <div class="has-header">
        <span class="sso-heading">SSO Settings</span>
        <div class="identity-providers-list">
          <div class="title-container row no-gutters">
            <div class="col-10 title">Identity Provider (IdP) List</div>
            <div class="col-2 text-right"><button @click="openAddAProviderModal()" class="btn btn-primary" :disabled="apiCallInProgress">Add IdP</button></div>
          </div>
          <div class="idp-table-wrapper" id="idp-table-wrapper">
            <table class="table" aria-describedby="configured-idps-list">
              <thead>
                <tr>
                  <th :class="[field.class, field.key]" v-for="(field, index) in tableHeaderFields" :key="`header-${field.key}-${index}`">
                    <span @click="sortList(field)">
                      {{ field.label }}
                      <span v-if="field.key === 'enabled'" class="info-icon">
                        <span class="fa fa-info-circle" :id="`popover-target-enabled`"></span>
                        <b-popover container="idp-table-wrapper" :target="`popover-target-enabled`" triggers="hover" placement="right">
                          Enabling this toggle allows<br />
                          users to login with this<br />
                          Identity Provider.
                        </b-popover>
                      </span>
                      <span v-if="(field.key === 'name' || field.key === 'regionName') && field.ascOrder != undefined">
                        <span class="sortUp" :class="{ 'disable-icon': !field.ascOrder }">
                          <span class="fa fa-angle-up" aria-hidden="true"></span>
                        </span>
                        <span class="sortDown" :class="{ 'disable-icon': !field.descOrder }">
                          <span class="fa fa-angle-down" aria-hidden="true"></span>
                        </span>
                      </span>
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(data, providerIdIndex) in idpProvidersList" :key="`idp-provider-${data.id}-${providerIdIndex}`">
                  <td>{{ data.name }}</td>
                  <td>
                    <span class="region">{{ data.regionName }}</span>
                  </td>
                  <td>
                    <div class="status-switch">
                      <div class="off">Off</div>
                      <div>
                        <label class="switch">
                          <input type="checkbox" v-model="data.status" v-on:change="getIdpToggleStatus(data)" />
                          <div class="slider round"></div>
                        </label>
                      </div>
                      <div class="on">On</div>
                    </div>
                  </td>
                  <td class="text-center">
                    <span class="edit">
                      <img src="static/img/pencil.svg" alt="Edit Provider" @click="openAddAProviderModal(data)" />
                    </span>
                  </td>
                  <td class="text-center">
                    <span class="delete">
                      <img src="static/img/trash-delete.svg" alt="Delete Provider" v-on:click="deleteIdentityProviders(data)" />
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    <addAProviderModal :apiCallInProgress="apiCallInProgress" @providerAdded="idpProviderAdded($event)" @apiCallComplete="apiCallComplete" ref="addAProviderModal" />
  </div>
</template>

<script>
import { SYSTEM_ADMIN, STUDY_ADMIN, SUPPORT } from "@/constants/authRoles";
import { mapGetters } from "vuex";
import { AclRule } from "vue-acl";
import addAProviderModal from "@/components/sso-login/add-a-provider-modal";
import MainHeader from "../common/main-header";
import swal from "sweetalert2";
import { orderBy } from "lodash";
import nprogress from "nprogress";
import "nprogress/nprogress.css";

export default {
  name: "idp-list",
  data() {
    return {
      idpProvidersList: null,
      apiCallInProgress: false,
      tableHeaderFields: [
        {
          key: "name",
          ascOrder: false,
          descOrder: false,
          label: "Provider Name",
          class: ""
        },
        {
          key: "regionName",
          ascOrder: false,
          descOrder: false,
          label: "Region",
          class: ""
        },
        {
          key: "enabled",
          label: "Enabled",
          class: ""
        },
        {
          key: "edit",
          label: "Edit",
          class: "text-center"
        },
        {
          key: "delete",
          label: "Delete",
          class: "text-center"
        }
      ]
    };
  },
  computed: {
    ...mapGetters(["user", "awsCognitoAuthentication"]),
    userAccess() {
      return new AclRule(this.user.userRole).generate();
    },
    canSysAdminAccess() {
      this.$acl.change(`${SYSTEM_ADMIN}`);
      return this.$acl.check("userAccess");
    },
    canStudyAdminAccess() {
      this.$acl.change(`${STUDY_ADMIN}`);
      return this.$acl.check("userAccess");
    },
    canSupportAccess() {
      this.$acl.change(`${SUPPORT}`);
      return this.$acl.check("userAccess");
    },
    canAccessSSOUserList() {
      return this.awsCognitoAuthentication && this.awsCognitoAuthentication === true && (this.canSysAdminAccess || this.canStudyAdminAccess || this.canSupportAccess);
    }
  },
  components: {
    mainheader: MainHeader,
    addAProviderModal
  },
  mounted() {
    if (!this.canAccessSSOUserList) {
      this.$router.push({
        name: "studies"
      });
    }
  },
  created() {
    this.getIdpProvidersList();
  },
  methods: {
    openAddAProviderModal(providerObject = null) {
      this.apiCallInProgress = true;
      this.$refs.addAProviderModal.show(providerObject);
    },
    apiCallComplete() {
      this.apiCallInProgress = false;
    },
    getIdpProvidersList() {
      nprogress.start();
      this.$api.getProviderListDetails().then(
        response => {
          nprogress.done();
          this.idpProvidersList = response.body.data;
        },
        error => {
          nprogress.done();
          this.$toastr.error(error.body && error.body.message ? error.body.message : "Error while getting IDP provider list");
        }
      );
    },
    idpProviderAdded(providerDetails) {
      this.idpProvidersList.push(providerDetails);
      this.getIdpProvidersList();
    },
    getIdpToggleStatus(data) {
      let pathParams = {
        id: data.id
      };
      let requestPayload = {
        status: data.status
      };

      this.$api.getToggleStatus(pathParams, requestPayload).then({}, error => {
        nprogress.done();
        this.$toastr.error(error.body && error.body.message ? error.body.message : "Error while updating IDP provider status");
      });
    },
    deleteIdentityProviders(data) {
      swal({
        title: `Confirm Delete`,
        html: `Are you sure you want to delete this IdP?`,
        type: "",
        showCancelButton: true,
        cancelButtonColor: "#f16559",
        confirmButtonColor: "#4c8ce4",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
        reverseButtons: true,
        customClass: "custom-alert"
      }).then(({ value }) => {
        if (value == true) {
          this.deleteProvider(data);
        }
      });
    },
    deleteProvider(data) {
      let pathParams = {
        id: data.id
      };
      this.$api.deleteProviderList(pathParams).then(
        response => {
          this.$toastr.success(`${data.name} removed`);
          this.getIdpProvidersList();
        },
        error => {
          this.$toastr.error(error.body && error.body.message ? error.body.message : "Error while deleting Provider List.");
        }
      );
    },
    sortList(field) {
      let sortValue = field.key;
      let sortOrder = "";
      field.ascOrder = !field.ascOrder;
      if (field.ascOrder) {
        sortOrder = "asc";
        field.descOrder = false;
      } else {
        field.descOrder = true;
        sortOrder = "desc";
      }

      //Reset sort flags for other keys
      this.tableHeaderFields.map(headerField => {
        if (headerField.key != sortValue && headerField.ascOrder != undefined) {
          headerField.ascOrder = false;
          headerField.descOrder = false;
        }
      });

      this.idpProvidersList = orderBy(this.idpProvidersList, [sortValue], [sortOrder]);
    }
  }
};
</script>

<style lang="scss" scoped>
.studies_bg {
  background-size: cover;
  height: 100%;
  min-height: 100vh;
  background-color: #f5f8fa;
  background-blend-mode: soft-light;

  .container {
    padding-top: 25px;
    padding-bottom: 24px;
    padding-right: 30px !important;
    padding-left: 30px !important;

    .sso-heading {
      font-size: 24px;
      font-family: Roboto-Medium;
      color: #666669;
      font-weight: 500;
    }

    .identity-providers-list {
      flex-grow: 1;
      padding: 24px 24px 0 24px;
      background-color: #fff;
      margin-top: 25px;
      min-height: calc(100vh - 200px);

      .title-container {
        align-items: center;
        margin-bottom: 24px;

        .title {
          font-family: Roboto-Bold;
          font-size: 16px;
          text-align: left;
          color: #666669;
          //min-width: 91.7%;
        }
      }

      .idp-table-wrapper {
        padding-bottom: 40px;
        table {
          thead {
            tr {
              th {
                font-family: Roboto-Medium;
                font-size: 12px;
                color: #666669;
                border-bottom: 1px solid #9b9b9b;
                border-top: 1px solid #9b9b9b;
                font-weight: 500;

                span {
                  cursor: pointer;
                }
                .info-icon {
                  color: #9b9b9b;
                  cursor: pointer;
                }

                .fa-info-circle:before {
                  font-size: 9px;
                }

                .sortUp {
                  position: absolute;
                  margin-top: -3px;
                  padding-left: 4px;

                  &.disable-icon {
                    color: #9b9b9b;
                  }
                }

                .sortDown {
                  position: absolute;
                  padding-left: 4px;
                  padding-top: 3px;

                  &.disable-icon {
                    color: #9b9b9b;
                  }
                }
              }
            }
          }

          tbody {
            tr {
              td {
                vertical-align: middle;
                padding-top: 9px;
                padding-bottom: 9px;
                border-bottom: 1px solid #dee2e6;

                .status-switch {
                  display: flex;
                  align-items: center;

                  .switch {
                    position: relative;
                    display: inline-block;
                    width: 32px;
                    height: 20px;
                    margin-left: 6px;
                    margin-right: 6px;
                    margin-bottom: -3px;
                    margin-top: 3px;
                  }

                  .switch input {
                    display: none;
                  }

                  .slider {
                    position: absolute;
                    cursor: pointer;
                    top: -1px;
                    left: -2px;
                    right: -3px;
                    bottom: -1px;
                    border: 2px solid #ccc;
                  }

                  .slider:before {
                    position: absolute;
                    content: "";
                    height: 16px;
                    width: 16px;
                    left: 2px;
                    bottom: 1.5px;
                    background-color: #ccc;
                  }

                  input:checked + .slider {
                    border: 2px solid #6bbb80;
                  }

                  input:checked + .slider:before {
                    content: "";
                    background-color: #6bbb80;
                  }

                  input:focus + .slider {
                    box-shadow: 0 0 1px #2196f3;
                  }

                  input:checked + .slider:before {
                    position: absolute;
                    color: #2ecc71;
                    text-align: center;
                    font-size: 20px;
                    -webkit-transform: translateX(14px);
                    -ms-transform: translateX(14px);
                    transform: translateX(14px);
                  }

                  /* Rounded sliders */
                  .slider.round {
                    border-radius: 34px;
                  }

                  .slider.round:before {
                    border-radius: 50%;
                  }

                  .checkbox {
                    width: 12px;
                    height: 12px;
                  }

                  .on {
                    opacity: 0.7;
                    font-family: Roboto-Regular;
                    font-size: 14px;
                    font-weight: normal;
                    font-stretch: normal;
                    font-style: normal;
                    line-height: 1.43;
                    letter-spacing: normal;
                    text-align: left;
                    color: #666669;
                  }

                  .off {
                    opacity: 0.7;
                    font-family: Roboto-Regular;
                    font-size: 14px;
                    font-weight: normal;
                    font-stretch: normal;
                    font-style: normal;
                    line-height: 1.43;
                    letter-spacing: normal;
                    text-align: left;
                    color: #666669;
                    display: inline-block;
                  }
                }

                .edit {
                  cursor: pointer;
                }

                .delete {
                  cursor: pointer;
                }
              }
            }
          }
        }
      }

      .btn-primary {
        padding: 6px 16px;
        border-radius: 4px;
        border: solid 1px #508ee1 !important;
        background-color: #4c8ce4 !important;
        cursor: pointer;
        font-family: Roboto-Medium;
        color: #ffffff;
        font-size: 14px;
        font-weight: 500;
      }
    }
  }
}
</style>

<style lang="scss">
.swal2-container.swal2-shown .swal2-modal.custom-alert .swal2-content {
  display: block !important;
}

.swal2-container .swal2-modal.custom-alert .swal2-content {
  font-size: 16px !important;
  margin-top: -4px !important;
}

.swal2-container .swal2-modal.custom-alert {
  width: 383px !important;
  height: 175px !important;
  padding: 24px !important;
  border-radius: 4px !important;
  background-color: #fff !important;
}

.swal2-container .swal2-modal.custom-alert .swal2-title {
  font-size: 24px !important;
  font-family: Roboto-Medium !important;
  font-weight: 500 !important;
  color: #666669 !important;
}

.swal2-container {
  .swal2-modal {
    &.custom-alert {
      .swal2-confirm {
        border-radius: 4px !important;
        border: solid 1px #b0bac9 !important;
        background-color: #4c8ce4 !important;
        color: #fff !important;
        font-size: 14px !important;
        font-weight: normal !important;
        outline: none !important;
        width: 69px !important;
        height: 40px !important;
        display: flex !important;
        justify-content: center !important;
        align-items: center !important;
        box-shadow: none !important;
        margin-top: 10px !important;
      }

      .swal2-cancel {
        border-radius: 4px !important;
        border: solid 1px #4c8ce4 !important;
        background-color: #fff !important;
        color: #666669 !important;
        font-size: 14px !important;
        font-weight: normal !important;
        outline: none !important;
        width: 69px !important;
        height: 40px !important;
        display: flex !important;
        justify-content: center !important;
        align-items: center !important;
        margin-top: 10px !important;
      }
    }
  }
}
.idp-table-wrapper {
  .popover {
    width: 144px;
    max-width: 144px;
    font-size: 10px;
    border-radius: 4px;
    color: #000;
    border: solid 1px #bdbdbd;
    line-height: normal;

    .popover-body {
      padding: 8px;
      color: #000;
      font-family: Roboto-Regular;
      border-radius: 4px;
    }
  }
}
</style>
